import React, { useContext } from 'react';
import {
  LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, AreaChart, Area
} from 'recharts';
import { FaCloudRain, FaSeedling, FaTemperatureHigh, FaTint } from 'react-icons/fa';
import { ThemeContext } from '../App';

export function TemperaturasChart({ dados, formatDate, isMobile }) {
  const { darkMode } = useContext(ThemeContext);

  return (
    <section className="forecast-chart-card is-featured is-temperature" style={{
      maxWidth: 1100, 
      margin: isMobile ? '0 0.75rem 1.5rem' : '0 auto 2rem', 
      background: darkMode ? '#232526' : 'white', 
      borderRadius: isMobile ? 12 : 18, 
      boxShadow: darkMode ? '0 2px 12px #1118' : '0 2px 12px rgba(25, 118, 210, 0.07)', 
      padding: isMobile ? '1.25rem 0.75rem' : '2.5rem 2rem', 
      marginBottom: isMobile ? '1.5rem' : '2rem' 
    }}>
      <div className="forecast-chart-heading">
        <span><FaTemperatureHigh aria-hidden="true" /></span>
        <div><small>Comportamento térmico</small><h2>Temperaturas</h2><p>Comparação diária entre mínima, média e máxima.</p></div>
      </div>
      <ResponsiveContainer width="100%" height={isMobile ? 240 : 300}>
        <LineChart data={dados} style={{ fontFamily: 'inherit' }}>
          <XAxis 
            dataKey="date" 
            tickFormatter={formatDate} 
            tick={{ fontSize: 13, fill: darkMode ? '#b0b0b0' : '#666' }} 
          />
          <YAxis 
            yAxisId="left" 
            label={{ value: '°C', angle: -90, position: 'insideLeft', fontSize: 13 }} 
            tick={{ fontSize: 13, fill: darkMode ? '#b0b0b0' : '#666' }} 
          />
          <Tooltip 
            labelFormatter={formatDate}
            contentStyle={{
              background: darkMode ? '#2a2a2a' : '#fff',
              border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
              borderRadius: 8,
              color: darkMode ? '#fff' : '#000'
            }}
          />
          <Legend wrapperStyle={{ color: darkMode ? '#b0b0b0' : '#666' }} />
          <Line yAxisId="left" type="monotone" dataKey="tmax" stroke="#ff1744" name="Temp Máx" strokeWidth={2} />
          <Line yAxisId="left" type="monotone" dataKey="tmean" stroke="#1976d2" name="Temp Média" strokeWidth={2} />
          <Line yAxisId="left" type="monotone" dataKey="tmin" stroke="#00bcd4" name="Temp Mín" strokeWidth={2} />
          <CartesianGrid stroke={darkMode ? '#444' : '#e3e3e3'} />
        </LineChart>
      </ResponsiveContainer>
    </section>
  );
}

export function PrecipitacaoChart({ dados, formatDate, isMobile }) {
  const { darkMode } = useContext(ThemeContext);

  return (
    <section className="forecast-chart-card is-rain" style={{
      maxWidth: 1100, 
      margin: isMobile ? '0 0.75rem 1.5rem' : '0 auto 2rem', 
      background: darkMode ? '#232526' : 'white', 
      borderRadius: isMobile ? 12 : 18, 
      boxShadow: darkMode ? '0 2px 12px #1118' : '0 2px 12px rgba(25, 118, 210, 0.07)', 
      padding: isMobile ? '1.25rem 0.75rem' : '2.5rem 2rem', 
      marginBottom: isMobile ? '1.5rem' : '2rem' 
    }}>
      <div className="forecast-chart-heading">
        <span><FaCloudRain aria-hidden="true" /></span>
        <div><small>Chuva prevista</small><h2>Precipitação</h2><p>Volume diário estimado em milímetros.</p></div>
      </div>
      <ResponsiveContainer width="100%" height={isMobile ? 230 : 260}>
        <LineChart data={dados} style={{ fontFamily: 'inherit' }}>
          <XAxis 
            dataKey="date" 
            tickFormatter={formatDate} 
            tick={{ fontSize: 13, fill: darkMode ? '#b0b0b0' : '#666' }} 
          />
          <YAxis 
            yAxisId="left" 
            label={{ value: 'mm/dia', angle: -90, position: 'insideLeft', fontSize: 13 }} 
            tick={{ fontSize: 13, fill: darkMode ? '#b0b0b0' : '#666' }} 
          />
          <Tooltip 
            labelFormatter={formatDate}
            contentStyle={{
              background: darkMode ? '#2a2a2a' : '#fff',
              border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
              borderRadius: 8,
              color: darkMode ? '#fff' : '#000'
            }}
          />
          <Legend wrapperStyle={{ color: darkMode ? '#b0b0b0' : '#666' }} />
          <Line yAxisId="left" dataKey="ppt" stroke="#43cea2" name="Precipitação" strokeWidth={2} />
          <CartesianGrid stroke={darkMode ? '#444' : '#e3e3e3'} />
        </LineChart>
      </ResponsiveContainer>
    </section>
  );
}

export function EtoChart({ dados, formatDate, isMobile }) {
  const { darkMode } = useContext(ThemeContext);

  return (
    <section className="forecast-chart-card is-eto" style={{
      maxWidth: 1100, 
      margin: isMobile ? '0 0.75rem 1.5rem' : '0 auto 2rem', 
      background: darkMode ? '#232526' : 'white', 
      borderRadius: isMobile ? 12 : 18, 
      boxShadow: darkMode ? '0 2px 12px #1118' : '0 2px 12px rgba(25, 118, 210, 0.07)', 
      padding: isMobile ? '1.25rem 0.75rem' : '2.5rem 2rem', 
      marginBottom: isMobile ? '1.5rem' : '2rem' 
    }}>
      <div className="forecast-chart-heading">
        <span><FaSeedling aria-hidden="true" /></span>
        <div><small>Demanda atmosférica</small><h2>Evapotranspiração</h2><p>ETo diária para apoiar o manejo hídrico.</p></div>
      </div>
      <ResponsiveContainer width="100%" height={isMobile ? 230 : 260}>
        <AreaChart data={dados} style={{ fontFamily: 'inherit' }}>
          <XAxis 
            dataKey="date" 
            tickFormatter={formatDate} 
            tick={{ fontSize: 13, fill: darkMode ? '#b0b0b0' : '#666' }} 
          />
          <YAxis 
            label={{ value: 'mm/dia', angle: -90, position: 'insideLeft', fontSize: 13 }} 
            tick={{ fontSize: 13, fill: darkMode ? '#b0b0b0' : '#666' }} 
          />
          <Tooltip 
            labelFormatter={formatDate}
            contentStyle={{
              background: darkMode ? '#2a2a2a' : '#fff',
              border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
              borderRadius: 8,
              color: darkMode ? '#fff' : '#000'
            }}
          />
          <Legend wrapperStyle={{ color: darkMode ? '#b0b0b0' : '#666' }} />
          <Area type="monotone" dataKey="eto_mm_day" stroke="#2e9e6e" fill="#b2f2bb" name="ETo (mm/dia)" strokeWidth={2} />
          <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#444' : '#e3e3e3'} />
        </AreaChart>
      </ResponsiveContainer>
    </section>
  );
}

export function UmidadeChart({ dados, formatDate, isMobile }) {
  const { darkMode } = useContext(ThemeContext);

  return (
    <section className="forecast-chart-card is-featured is-humidity" style={{
      maxWidth: 1100, 
      margin: isMobile ? '0 0.75rem 1.5rem' : '0 auto 2rem', 
      background: darkMode ? '#232526' : 'white', 
      borderRadius: isMobile ? 12 : 18, 
      boxShadow: darkMode ? '0 2px 12px #1118' : '0 2px 12px rgba(25, 118, 210, 0.07)', 
      padding: isMobile ? '1.25rem 0.75rem' : '2.5rem 2rem', 
      marginBottom: isMobile ? '1.5rem' : '2rem' 
    }}>
      <div className="forecast-chart-heading">
        <span><FaTint aria-hidden="true" /></span>
        <div><small>Condição do ar</small><h2>Umidade relativa</h2><p>Variação diária da umidade prevista.</p></div>
      </div>
      <ResponsiveContainer width="100%" height={isMobile ? 240 : 280}>
        <LineChart data={dados} style={{ fontFamily: 'inherit' }}>
          <XAxis 
            dataKey="date" 
            tickFormatter={formatDate} 
            tick={{ fontSize: 13, fill: darkMode ? '#b0b0b0' : '#666' }} 
          />
          <YAxis 
            yAxisId="left" 
            label={{ value: '%', angle: -90, position: 'insideLeft', fontSize: 13 }} 
            tick={{ fontSize: 13, fill: darkMode ? '#b0b0b0' : '#666' }} 
            domain={[0, 100]} 
          />
          <Tooltip 
            labelFormatter={formatDate}
            contentStyle={{
              background: darkMode ? '#2a2a2a' : '#fff',
              border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
              borderRadius: 8,
              color: darkMode ? '#fff' : '#000'
            }}
          />
          <Legend wrapperStyle={{ color: darkMode ? '#b0b0b0' : '#666' }} />
          <Line yAxisId="left" dataKey="ur" stroke="#00bcd4" name="Umidade Relativa" strokeWidth={2} />
          <CartesianGrid stroke={darkMode ? '#444' : '#e3e3e3'} />
        </LineChart>
      </ResponsiveContainer>
    </section>
  );
}
